import { clsx } from 'clsx'

interface TimeGridProps {
  label: string
  items: number[]
  selectedItem: number | null
  gridCols?: string
  buttonSizeClass?: string
  onSelect: (item: number) => void
}

export function TimeGrid({
  label,
  items,
  selectedItem,
  gridCols = 'grid-cols-6',
  buttonSizeClass = 'py-1.5 text-sm rounded',
  onSelect,
}: TimeGridProps): JSX.Element {
  return (
    <div className="mb-3">
      <div className="text-xs font-medium text-muted-foreground mb-2">{label}</div>
      <div className={clsx('grid gap-1', gridCols)}>
        {items.map((item) => (
          <TimeGridButton
            key={item}
            buttonSizeClass={buttonSizeClass}
            isSelected={selectedItem === item}
            value={item}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  )
}

interface TimeGridButtonProps {
  value: number
  isSelected: boolean
  buttonSizeClass: string
  onSelect: (value: number) => void
}

function TimeGridButton({ value, isSelected, buttonSizeClass, onSelect }: TimeGridButtonProps): JSX.Element {
  return (
    <button
      className={clsx(
        buttonSizeClass,
        'text-center transition-colors duration-150',
        isSelected
          ? 'bg-primary-solid text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent'
      )}
      type="button"
      onClick={() => onSelect(value)}
    >
      {value.toString().padStart(2, '0')}
    </button>
  )
}

interface PeriodSelectorProps {
  selectedPeriod: 'AM' | 'PM'
  buttonSizeClass?: string
  gapClass?: string
  onSelect: (period: 'AM' | 'PM') => void
}

export function PeriodSelector({
  selectedPeriod,
  buttonSizeClass = 'py-1.5 text-sm font-medium rounded',
  gapClass = 'gap-2',
  onSelect,
}: PeriodSelectorProps): JSX.Element {
  return (
    <div className="mb-3">
      <div className="text-xs font-medium text-muted-foreground mb-2">Period</div>
      <div className={clsx('grid grid-cols-2', gapClass)}>
        {(['AM', 'PM'] as const).map((period) => (
          <button
            key={period}
            className={clsx(
              buttonSizeClass,
              'transition-colors duration-150',
              selectedPeriod === period
                ? 'bg-primary-solid text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent border border-border'
            )}
            type="button"
            onClick={() => onSelect(period)}
          >
            {period}
          </button>
        ))}
      </div>
    </div>
  )
}

interface NowButtonProps {
  className?: string
  onClick: () => void
}

export function NowButton({ className, onClick }: NowButtonProps): JSX.Element {
  return (
    <div className="pt-3 border-t border-border">
      <button
        className={clsx(
          className ?? 'w-full px-3 py-1.5 text-sm font-medium rounded text-muted-foreground hover:bg-accent transition-colors duration-150'
        )}
        type="button"
        onClick={onClick}
      >
        Now
      </button>
    </div>
  )
}
